// frontend/src/pages/shops/Services.js
// TODO FEATURE REQUEST: Add service import from CSV/Excel
// - Allow business owners to import service templates from CSV files
// - Similar to professional service import (see StaffServices.js handleImportServices)
// - Add "Import Services" button near service template actions
// - Use same CSV format: name, description, price, duration, category
// - Validate all fields before creating templates
// - Templates created as isTemplate: true with no staffId
// - Link to sample-services.csv for download
// - Useful for businesses migrating from other systems or bulk template creation
import React, { useState, useEffect } from "react";
import axios from "axios";
import SidebarLayout from "./SidebarLayout";
import ServiceStaffAssignment from "../../components/ServiceStaffAssignment";
import ServiceImportModal from "../../components/ServiceImportModal";
import "./shopStyles.css";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";

const Services = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [templates, setTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shopId, setShopId] = useState(null); // Keep for backward compatibility
  const [businessId, setBusinessId] = useState(null); // Templates are business-level

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  // Modals
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentTemplate, setCurrentTemplate] = useState(null);

  // Form state with image file
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    durationMinutes: 30,
    category: "haircut",
    price: "",
    pricingType: "fixed",
    maxPrice: "",
    hourlyRate: "",
    pricePerUnit: "",
    perUnitMinutes: "",
    unitLabel: "",
    maxUnitsPerBooking: 10,
    maxHoursPerBooking: 8,
    priceTiers: [],
    requiresConsultation: false,
    applicableTo: [],
    platformTemplateId: null, // Track platform template usage
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [professionalTypes, setProfessionalTypes] = useState([]);
  const [serviceCategories, setServiceCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);

  // Platform Templates State
  const [showTemplates, setShowTemplates] = useState(false);
  const [platformTemplates, setPlatformTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [templatesError, setTemplatesError] = useState("");
  const [templateSearchTerm, setTemplateSearchTerm] = useState("");
  const [templateCategoryFilter, setTemplateCategoryFilter] = useState("all");
  const [selectedTemplates, setSelectedTemplates] = useState([]); // For multiple selection
  const [showQuickEditPanel, setShowQuickEditPanel] = useState(false); // Quick edit panel
  const [newlyAddedTemplates, setNewlyAddedTemplates] = useState([]); // Templates just added

  // Bulk Delete State
  const [selectedServices, setSelectedServices] = useState([]); // For bulk delete

  // Custom Modal State
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteModalData, setDeleteModalData] = useState({
    services: [],
    onConfirm: null,
  });
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmModalData, setConfirmModalData] = useState({
    message: "",
    onConfirm: null,
    onCancel: null,
  });

  // Alert state
  const [alert, setAlert] = useState({ show: false, message: "", type: "" });

  // Staff Assignment Modal state
  const [showStaffAssignment, setShowStaffAssignment] = useState(false);
  const [selectedServiceForStaff, setSelectedServiceForStaff] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);

  // Mobile detection
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Fetch shop ID and templates
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("ogestyle_token");
      if (!token) {
        window.location.href = "/login";
        return;
      }

      const headers = { Authorization: `Bearer ${token}` };

      // PHASE 4 FIX: Use my-shops endpoint instead of User.shopIds
      const shopsResponse = await axios.get(
        `${API_BASE_URL}/api/shop/my-shops`,
        { headers },
      );
      const shops = shopsResponse.data?.shops || shopsResponse.data || [];

      if (!shops.length) {
        setLoading(false);
        return;
      }

      // Use first shop as default
      const userShopId = shops[0]._id;
      const userBusinessId = shops[0].businessId?._id || shops[0].businessId;
      setShopId(userShopId);
      setBusinessId(userBusinessId);
      // Fetch service templates by businessId (templates are business-level resources)
      const templatesResponse = await axios.get(
        `${API_BASE_URL}/api/services/templates/business/${userBusinessId}`,
        { headers },
      );
      setTemplates(templatesResponse.data);
      setFilteredTemplates(templatesResponse.data);

      // Fetch professional types for templates (business-wide)
      try {
        const typesResponse = await axios.get(
          `${API_BASE_URL}/api/platform-settings/professional-types/public`,
          { headers },
        );
        setProfessionalTypes(typesResponse.data.types || []);
      } catch (err) {
      }

      // Fetch business info to get business categories (type array)
      try {
        const businessResponse = await axios.get(
          `${API_BASE_URL}/api/business/${userBusinessId}`,
          { headers },
        );
        const business = businessResponse.data;

        // Business.type is an array of category values (e.g., ["salon", "barbershop", "hair-studio"])
        const businessTypes = business?.type || [];
        // Fetch service categories for ALL business types
        if (businessTypes.length > 0) {
          const categoriesResponse = await axios.get(
            `${API_BASE_URL}/api/platform-settings/service-categories?businessCategories=${businessTypes.join(",")}`,
            { headers },
          );
          const categories = categoriesResponse.data.categories || [];
          setServiceCategories(categories);
        } else {
          setServiceCategories([
            { value: "haircut", label: "Haircut" },
            { value: "color", label: "Color" },
            { value: "styling", label: "Styling" },
            { value: "treatment", label: "Treatment" },
            { value: "spa", label: "Spa" },
            { value: "nails", label: "Nails" },
            { value: "makeup", label: "Makeup" },
            { value: "waxing", label: "Waxing" },
            { value: "other", label: "Other" },
          ]);
        }
      } catch (err) {
        // Fallback to default categories if API fails
        setServiceCategories([
          { value: "haircut", label: "Haircut" },
          { value: "color", label: "Color" },
          { value: "styling", label: "Styling" },
          { value: "treatment", label: "Treatment" },
          { value: "spa", label: "Spa" },
          { value: "nails", label: "Nails" },
          { value: "makeup", label: "Makeup" },
          { value: "waxing", label: "Waxing" },
          { value: "other", label: "Other" },
        ]);
      } finally {
        setLoadingCategories(false);
      }

      setLoading(false);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem("ogestyle_token");
        window.location.href = "/login";
      }
      setLoading(false);
    }
  };

  // Apply filters
  useEffect(() => {
    let filtered = [...templates];

    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (template) =>
          template.name.toLowerCase().includes(search) ||
          (template.description &&
            template.description.toLowerCase().includes(search)),
      );
    }

    // Category filter
    if (categoryFilter !== "all") {
      filtered = filtered.filter(
        (template) => template.category === categoryFilter,
      );
    }

    // Status filter
    if (statusFilter === "active") {
      filtered = filtered.filter((template) => template.isActive !== false);
    } else if (statusFilter === "inactive") {
      filtered = filtered.filter((template) => template.isActive === false);
    }

    setFilteredTemplates(filtered);
  }, [templates, searchTerm, categoryFilter, statusFilter]);

  // Show alert
  const showAlert = (message, type = "success") => {
    setAlert({ show: true, message, type });
    setTimeout(() => setAlert({ show: false, message: "", type: "" }), 5000);
  };

  // Handle create new template
  const handleCreate = () => {
    setIsEditing(false);
    setCurrentTemplate(null);
    setFormData({
      name: "",
      description: "",
      durationMinutes: 30,
      category: "haircut",
      price: "",
      pricingType: "fixed",
      maxPrice: "",
      hourlyRate: "",
      pricePerUnit: "",
      perUnitMinutes: "",
      unitLabel: "",
      maxUnitsPerBooking: 10,
      maxHoursPerBooking: 8,
      priceTiers: [],
      requiresConsultation: false,
      applicableTo: [],
    });
    setImageFile(null);
    setImagePreview(null);
    setShowModal(true);
  };

  // Handle edit template
  const handleEdit = (template) => {
    setIsEditing(true);
    setCurrentTemplate(template);
    setFormData({
      name: template.name,
      description: template.description || "",
      durationMinutes: template.durationMinutes,
      category: template.category,
      price: template.price || "",
      pricingType: template.pricingType || "fixed",
      maxPrice: template.maxPrice || "",
      hourlyRate: template.hourlyRate || "",
      pricePerUnit: template.pricePerUnit || "",
      perUnitMinutes: template.perUnitMinutes || "",
      unitLabel: template.unitLabel || "",
      maxUnitsPerBooking: template.maxUnitsPerBooking || 10,
      maxHoursPerBooking: template.maxHoursPerBooking || 8,
      priceTiers: Array.isArray(template.priceTiers)
        ? template.priceTiers.map((tier) => ({
            label: tier.label || "",
            price: tier.price ?? "",
            durationMinutes: tier.durationMinutes ?? "",
          }))
        : [],
      requiresConsultation: template.requiresConsultation || false,
      applicableTo: template.applicableTo || [],
    });
    setImageFile(null);
    // Check if image is absolute URL (Azure Blob) or relative path
    const isAbsoluteUrl =
      template.serviceImage &&
      (template.serviceImage.startsWith("http://") ||
        template.serviceImage.startsWith("https://"));
    setImagePreview(
      template.serviceImage
        ? isAbsoluteUrl
          ? template.serviceImage
          : `${API_BASE_URL}${template.serviceImage}`
        : null,
    );
    setShowModal(true);
  };

  // Handle applicableTo toggle
  const handleApplicableToToggle = (typeValue) => {
    setFormData((prev) => {
      const isSelected = prev.applicableTo.includes(typeValue);
      const newApplicableTo = isSelected
        ? prev.applicableTo.filter((t) => t !== typeValue)
        : [...prev.applicableTo, typeValue];
      return { ...prev, applicableTo: newApplicableTo };
    });
  };

  // Handle image file selection
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        showAlert("Image must be less than 5MB", "error");
        return;
      }
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Fetch Platform Templates
  const fetchPlatformTemplates = async () => {
    setLoadingTemplates(true);
    setTemplatesError("");

    try {
      const token = localStorage.getItem("ogestyle_token");
      if (!token) return;

      const headers = { Authorization: `Bearer ${token}` };

      // Get business types from my shops
      const shopsResponse = await axios.get(
        `${API_BASE_URL}/api/shop/my-shops`,
        { headers },
      );
      const shops = shopsResponse.data?.shops || shopsResponse.data || [];

      // Collect business types from all shops
      let businessTypes = [];
      if (shops.length > 0) {
        const business = shops[0].businessId || shops[0];
        businessTypes = business.type || [];
      }

      // Fetch templates filtered by business types
      const params = new URLSearchParams();
      if (businessTypes.length > 0) {
        params.append("businessTypes", businessTypes.join(","));
      }
      params.append("limit", "100");

      const response = await axios.get(
        `${API_BASE_URL}/api/platform-settings/service-templates/public?${params.toString()}`,
        { headers },
      );

      setPlatformTemplates(response.data.templates || []);
    } catch (error) {
      setTemplatesError("Failed to load templates. Please try again.");
    } finally {
      setLoadingTemplates(false);
    }
  };

  // Toggle template selection (checkbox)
  const getTemplateId = (template) => template?._id || template?.id;

  const handleToggleTemplateSelection = (templateId) => {
    setSelectedTemplates((prev) =>
      prev.includes(templateId)
        ? prev.filter((id) => id !== templateId)
        : [...prev, templateId],
    );
  };

  // Bulk add selected templates
  const handleBulkAddTemplates = async () => {
    if (selectedTemplates.length === 0) {
      showAlert("Please select at least one template", "error");
      return;
    }

    try {
      const token = localStorage.getItem("ogestyle_token");
      const headers = { Authorization: `Bearer ${token}` };

      // Get selected template objects
      const templatesToAdd = platformTemplates.filter((t) =>
        selectedTemplates.includes(getTemplateId(t)),
      );

      if (templatesToAdd.length === 0) {
        showAlert("Selected templates could not be resolved. Please reselect.", "error");
        return;
      }

      // Check for duplicates - case-insensitive name matching
      const existingNames = templates.map((t) => t.name.toLowerCase().trim());
      const duplicates = [];
      const uniqueTemplates = [];

      for (const template of templatesToAdd) {
        const templateName = template.name.toLowerCase().trim();
        if (existingNames.includes(templateName)) {
          duplicates.push(template.name);
        } else {
          uniqueTemplates.push(template);
        }
      }

      // Show warning if duplicates found
      if (duplicates.length > 0) {
        await new Promise((resolve) => {
          setConfirmModalData({
            message: (
              <div>
                <p style={{ marginBottom: "12px", fontWeight: "600" }}>
                  The following service(s) already exist and will be skipped:
                </p>
                <ul
                  style={{
                    marginLeft: "20px",
                    marginBottom: "16px",
                    color: "#dc2626",
                  }}
                >
                  {duplicates.map((name, i) => (
                    <li key={i}>{name}</li>
                  ))}
                </ul>
                <p style={{ fontWeight: "600", color: "#059669" }}>
                  {uniqueTemplates.length} new service(s) will be added.
                  Continue?
                </p>
              </div>
            ),
            onConfirm: () => {
              setShowConfirmModal(false);
              resolve(true);
            },
            onCancel: () => {
              setShowConfirmModal(false);
              resolve(false);
            },
          });
          setShowConfirmModal(true);
        }).then((shouldContinue) => {
          if (!shouldContinue) return;
          continueAddingTemplates();
        });
        return;
      }

      continueAddingTemplates();

      async function continueAddingTemplates() {
        if (uniqueTemplates.length === 0) {
          showAlert(
            "All selected services already exist. No duplicates created.",
            "warning",
          );
          setSelectedTemplates([]);
          return;
        }

        // Create all unique templates
        const createdTemplates = [];
        for (const template of uniqueTemplates) {
          const submitData = new FormData();
          submitData.append("name", template.name);
          submitData.append("description", template.description || "");
          submitData.append(
            "durationMinutes",
            template.suggestedDuration || 30,
          );
          submitData.append("category", template.category);
          submitData.append("businessId", businessId);
          submitData.append(
            "applicableTo",
            JSON.stringify(template.applicableTo || []),
          );
          submitData.append("pricingType", template.pricingType || "fixed");
          submitData.append(
            "requiresConsultation",
            template.requiresConsultation || false,
          );
          submitData.append("platformTemplateId", getTemplateId(template));

          if (template.suggestedPrice !== undefined && template.suggestedPrice !== null && template.suggestedPrice !== "") {
            const price = Number(String(template.suggestedPrice).replace(/[^0-9.]/g, ""));
            if (Number.isFinite(price) && price > 0) {
              submitData.append("price", price);
            }
          }
          if (template.pricingType === "range" && template.maxPrice) {
            const maxPrice = Number(String(template.maxPrice).replace(/[^0-9.]/g, ""));
            if (Number.isFinite(maxPrice) && maxPrice > 0) {
              submitData.append("maxPrice", maxPrice);
            }
          }
          // A tiered platform template was copied WITHOUT its options, so the new shop template arrived
          // as a range service with nothing to choose — and therefore no price a customer could pick.
          // maxPrice alone describes the band, it does not create the options.
          if (
            template.pricingType === "range" &&
            Array.isArray(template.priceTiers) &&
            template.priceTiers.length > 0
          ) {
            const usableTiers = template.priceTiers
              .map((tier) => ({
                label: String(tier?.label || "").trim(),
                price: Number(tier?.price),
                durationMinutes: Number(tier?.durationMinutes),
              }))
              .filter(
                (tier) =>
                  tier.label && Number.isFinite(tier.price) && tier.price > 0,
              );
            if (usableTiers.length > 0) {
              submitData.append("priceTiers", JSON.stringify(usableTiers));
            }
          }
          // Per-unit templates lost every field that makes the type usable, exactly like the tiered case.
          if (template.pricingType === "per_unit") {
            const perUnit = Number(
              String(template.pricePerUnit ?? "").replace(/[^0-9.]/g, ""),
            );
            if (Number.isFinite(perUnit) && perUnit > 0) {
              submitData.append("pricePerUnit", perUnit);
            }
            if (template.perUnitMinutes) {
              submitData.append(
                "perUnitMinutes",
                parseInt(template.perUnitMinutes, 10),
              );
            }
            if (template.unitLabel) {
              submitData.append("unitLabel", template.unitLabel);
            }
            if (template.maxUnitsPerBooking) {
              submitData.append(
                "maxUnitsPerBooking",
                parseInt(template.maxUnitsPerBooking, 10),
              );
            }
          }
          if (template.pricingType === "hourly" && template.hourlyRate) {
            const hourlyRate = Number(String(template.hourlyRate).replace(/[^0-9.]/g, ""));
            if (Number.isFinite(hourlyRate) && hourlyRate > 0) {
              submitData.append("hourlyRate", hourlyRate);
            }
          }
          if (template.pricingType === "hourly" && template.maxHoursPerBooking) {
            submitData.append(
              "maxHoursPerBooking",
              parseInt(template.maxHoursPerBooking, 10),
            );
          }

          const response = await axios.post(
            `${API_BASE_URL}/api/services/templates`,
            submitData,
            { headers },
          );
          // Accept either direct template payload or wrapped payload.
          const createdTemplate =
            response.data?.template || response.data?.service || response.data;
          if (createdTemplate && (createdTemplate._id || createdTemplate.id)) {
            createdTemplates.push(createdTemplate);
          }
        }

        if (createdTemplates.length === 0) {
          showAlert("Services were not created. Please try again.", "error");
          return;
        }

        // Success!
        const message =
          duplicates.length > 0
            ? `✓ ${createdTemplates.length} service${createdTemplates.length > 1 ? "s" : ""} added! (${duplicates.length} duplicate${duplicates.length > 1 ? "s" : ""} skipped)`
            : `✓ ${createdTemplates.length} service${createdTemplates.length > 1 ? "s" : ""} added successfully!`;

        showAlert(message, "success");

        // Show the new services immediately, even before the refresh completes.
        setTemplates((prev) => [...createdTemplates, ...prev]);

        // Show quick edit panel
        setNewlyAddedTemplates(createdTemplates);
        setShowQuickEditPanel(true);

        // Clear selection and refresh templates
        setSelectedTemplates([]);
        fetchData();
      }
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Failed to add templates. Please try again.";
      showAlert(message, "error");
    }
  };

  // Toggle service selection for bulk delete
  const handleToggleServiceSelection = (serviceId) => {
    setSelectedServices((prev) => {
      if (prev.includes(serviceId)) {
        return prev.filter((id) => id !== serviceId);
      } else {
        return [...prev, serviceId];
      }
    });
  };

  // Delete service template (single)
  const handleDeleteTemplate = async (template, skipConfirm = false) => {
    if (!skipConfirm) {
      const deleteType = window.confirm(
        `Delete "${template.name}"?\n\n` +
          `Click OK to DEACTIVATE (can be reactivated later)\n` +
          `Click Cancel to go back\n\n` +
          `Note: To permanently delete, use the bulk delete option.`,
      );

      if (!deleteType) {
        return;
      }
    }

    try {
      const token = localStorage.getItem("ogestyle_token");
      const headers = { Authorization: `Bearer ${token}` };

      // Default to soft delete for single template
      await axios.delete(
        `${API_BASE_URL}/api/services/templates/${template._id}`,
        { headers },
      );

      showAlert(
        `Service "${template.name}" deactivated successfully`,
        "success",
      );
      fetchData(); // Refresh the list
    } catch (error) {
      showAlert("Failed to delete service. Please try again.", "error");
    }
  };

  // Bulk delete templates
  const handleBulkDelete = () => {
    if (selectedServices.length === 0) {
      showAlert("Please select at least one service to delete", "error");
      return;
    }

    const selectedTemplates = templates.filter((t) =>
      selectedServices.includes(t._id),
    );

    // Show custom modal for delete type selection
    setDeleteModalData({
      services: selectedTemplates,
      onConfirm: async (deleteType) => {
        setShowDeleteModal(false);
        await executeBulkDelete(deleteType);
      },
    });
    setShowDeleteModal(true);
  };

  // Execute bulk delete after modal confirmation
  const executeBulkDelete = async (deleteType) => {
    const isPermanent = deleteType === "PERMANENT";

    try {
      const token = localStorage.getItem("ogestyle_token");
      const headers = { Authorization: `Bearer ${token}` };

      let successCount = 0;
      let failCount = 0;

      for (const serviceId of selectedServices) {
        try {
          const url = isPermanent
            ? `${API_BASE_URL}/api/services/templates/${serviceId}?permanent=true`
            : `${API_BASE_URL}/api/services/templates/${serviceId}`;

          await axios.delete(url, { headers });
          successCount++;
        } catch (error) {
          failCount++;
        }
      }

      const action = isPermanent ? "permanently deleted" : "deactivated";
      if (failCount === 0) {
        showAlert(
          `✓ ${successCount} service(s) ${action} successfully`,
          "success",
        );
      } else {
        showAlert(
          `${successCount} service(s) ${action}, ${failCount} failed`,
          "warning",
        );
      }

      setSelectedServices([]); // Clear selection
      fetchData(); // Refresh the list
    } catch (error) {
      showAlert("Failed to delete services. Please try again.", "error");
    }
  };

  // Toggle templates browser
  const handleToggleTemplates = () => {
    if (!showTemplates && platformTemplates.length === 0) {
      fetchPlatformTemplates();
    }
    setShowTemplates(!showTemplates);
  };

  function normalizeCategoryValue(value) {
    return String(value || "")
      .trim()
      .toLowerCase();
  }

  const templateCategoryOptions = Array.from(
    new Map(
      platformTemplates
        .filter((template) => template && template.category)
        .map((template) => {
          const normalized = normalizeCategoryValue(template.category);
          return [normalized, { value: normalized, label: toUserFriendly(template.category) }];
        }),
    ).values(),
  ).sort((a, b) => a.label.localeCompare(b.label));

  // Filter templates
  const filteredPlatformTemplates = platformTemplates.filter((template) => {
    // Safety check
    if (!template || !template.name) {
      return false;
    }

    const templateCategory = normalizeCategoryValue(template.category);
    const selectedCategory = normalizeCategoryValue(templateCategoryFilter);
    if (selectedCategory !== "all" && templateCategory !== selectedCategory) {
      return false;
    }

    if (templateSearchTerm) {
      const search = templateSearchTerm.toLowerCase();
      return (
        template.name.toLowerCase().includes(search) ||
        (template.description &&
          template.description.toLowerCase().includes(search))
      );
    }

    return true;
  });

  useEffect(() => {
    if (templateCategoryFilter === "all") {
      return;
    }

    const categoryStillExists = platformTemplates.some(
      (template) =>
        normalizeCategoryValue(template?.category) ===
        normalizeCategoryValue(templateCategoryFilter),
    );

    if (!categoryStillExists) {
      setTemplateCategoryFilter("all");
    }
  }, [platformTemplates, templateCategoryFilter]);

  // Utility function to convert text to user-friendly Title Case
  function toUserFriendly(text) {
    if (!text) return "";
    return text
      .replace(/[-_]/g, " ")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  }

  // Submit create/edit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !formData.name ||
      (formData.pricingType !== "hourly" &&
        formData.pricingType !== "range" &&
        !formData.durationMinutes)
    ) {
      showAlert("Please fill in all required fields", "error");
      return;
    }

    try {
      const token = localStorage.getItem("ogestyle_token");
      const headers = { Authorization: `Bearer ${token}` };

      // Use FormData for image upload
      const submitData = new FormData();
      submitData.append("name", formData.name);
      submitData.append("description", formData.description);
      // Duration field is hidden for hourly/range - append the derived value here
      // so exactly one durationMinutes value is ever sent.
      if (
        formData.pricingType !== "hourly" &&
        formData.pricingType !== "range"
      ) {
        submitData.append("durationMinutes", formData.durationMinutes);
      }
      submitData.append("category", formData.category);
      submitData.append("businessId", businessId); // Templates are business-level resources
      submitData.append("applicableTo", JSON.stringify(formData.applicableTo)); // Add professional types
      submitData.append("pricingType", formData.pricingType);
      submitData.append("requiresConsultation", formData.requiresConsultation);

      // ONLY for the types that do not compute their own price.
      //
      // `per_unit`, `hourly` and `range` each append their own `price` below. This line used to run
      // unconditionally as well, so those three sent `price` TWICE; FormData keeps both, the server
      // received ["75","180"], and `parseFloat("75,180")` silently returned the FIRST — the stale value.
      // That is why a template saved with three priced options still displayed its old $75. The
      // durationMinutes append below already carries a guard for this exact trap; `price` did not.
      if (
        formData.pricingType !== "per_unit" &&
        formData.pricingType !== "hourly" &&
        formData.pricingType !== "range" &&
        formData.price
      ) {
        submitData.append("price", parseFloat(formData.price));
      }

      // Pricing type specific fields
      // F2: hourly = rate x hours; range = customer picks one of the options
      if (formData.pricingType === "hourly") {
        const rate = parseFloat(formData.hourlyRate);
        if (Number.isFinite(rate)) {
          submitData.append("hourlyRate", rate);
          submitData.append("price", rate);
        }
        submitData.append(
          "maxHoursPerBooking",
          parseInt(formData.maxHoursPerBooking || 8, 10),
        );
        // Duration field is hidden for hourly - the engine bills 60 min per hour.
        submitData.append("durationMinutes", 60);
      }
      if (formData.pricingType === "range") {
        const usableTiers = (formData.priceTiers || [])
          .map((tier) => ({
            label: String(tier.label || "").trim(),
            price: parseFloat(tier.price),
            durationMinutes: parseInt(tier.durationMinutes, 10),
          }))
          .filter((tier) => tier.label && Number.isFinite(tier.price));

        if (usableTiers.length < 2) {
          showAlert(
            "Add at least 2 priced options (for example: Shoulder length $150, Mid-back $220).",
            "error",
          );
          return;
        }

        const missingMinutes = usableTiers.some(
          (tier) =>
            !Number.isFinite(tier.durationMinutes) || tier.durationMinutes < 5,
        );
        if (missingMinutes) {
          showAlert(
            "Give every option a duration of at least 5 minutes.",
            "error",
          );
          return;
        }

        submitData.append("priceTiers", JSON.stringify(usableTiers));
        // The stored band + legacy price alias come from the options
        const tierPrices = usableTiers.map((tier) => tier.price);
        submitData.append("price", Math.min(...tierPrices));
        submitData.append("maxPrice", Math.max(...tierPrices));
        // Duration field is hidden for range - use the longest option length.
        submitData.append(
          "durationMinutes",
          Math.max(...usableTiers.map((t) => Number(t.durationMinutes) || 0)),
        );
      }
      // F1: per-unit pricing fields
      if (formData.pricingType === "per_unit") {
        const unitPrice = parseFloat(formData.pricePerUnit);

        // Fail here, readably. `parseFloat("")` is NaN, which was appended as the literal string "NaN"
        // and came back from the server as a bare 400 ("Price per unit must be greater than 0") — a save
        // that looks like nothing happened at all.
        if (!Number.isFinite(unitPrice) || unitPrice <= 0) {
          showAlert("Enter a price per unit greater than 0.", "error");
          return;
        }

        submitData.append("pricePerUnit", unitPrice);

        // `price` is the legacy alias that price-based call sites and the service card read, so it has to
        // track the UNIT price. This previously preferred `formData.price` whenever it was truthy — and
        // it always is, because editing loads the template's existing `price` into it — so saving a new
        // unit price left the OLD figure on screen and the edit looked like it had not saved. Mirrors
        // hourly (`price = rate`) and range (`price = min(tier)`).
        submitData.append("price", unitPrice);

        if (formData.perUnitMinutes) {
          submitData.append("perUnitMinutes", parseInt(formData.perUnitMinutes, 10));
        }
        if (formData.unitLabel) {
          submitData.append("unitLabel", formData.unitLabel);
        }
        submitData.append(
          "maxUnitsPerBooking",
          parseInt(formData.maxUnitsPerBooking || 10, 10),
        );
      }

      // Track platform template usage
      if (formData.platformTemplateId) {
        submitData.append("platformTemplateId", formData.platformTemplateId);
      }

      // Image upload - COMMENTED OUT PER USER REQUEST
      /*
      if (imageFile) {
        submitData.append("serviceImage", imageFile);
      }
      */
      if (isEditing) {
        // Update existing template
        await axios.patch(
          `${API_BASE_URL}/api/services/templates/${currentTemplate._id}`,
          submitData,
          { headers },
        );
        showAlert("Service template updated successfully", "success");
      } else {
        // Create new template
        await axios.post(`${API_BASE_URL}/api/services/templates`, submitData, {
          headers,
        });
        showAlert("Service template created successfully", "success");
      }

      setShowModal(false);
      fetchData();
    } catch (error) {
      showAlert(
        error.response?.data?.message || "Failed to save service template",
        "error",
      );
    }
  };

  // Handle deactivate
  const handleDeactivate = async (templateId) => {
    if (
      !window.confirm(
        "Are you sure you want to deactivate this service template?",
      )
    ) {
      return;
    }

    try {
      const token = localStorage.getItem("ogestyle_token");
      const headers = { Authorization: `Bearer ${token}` };

      await axios.delete(
        `${API_BASE_URL}/api/services/templates/${templateId}`,
        { headers },
      );

      showAlert("Service template deactivated successfully", "success");
      fetchData();
    } catch (error) {
      showAlert("Failed to deactivate service template", "error");
    }
  };

  // Handle reactivate
  const handleReactivate = async (template) => {
    try {
      const token = localStorage.getItem("ogestyle_token");
      const headers = { Authorization: `Bearer ${token}` };

      await axios.patch(
        `${API_BASE_URL}/api/services/templates/${template._id}`,
        { isActive: true },
        { headers },
      );

      showAlert("Service template reactivated successfully", "success");
      fetchData();
    } catch (error) {
      showAlert("Failed to reactivate service template", "error");
    }
  };

  // Handle staff assignment modal
  const handleOpenStaffAssignment = (template) => {
    setSelectedServiceForStaff(template);
    setShowStaffAssignment(true);
  };

  const handleCloseStaffAssignment = () => {
    setShowStaffAssignment(false);
    setSelectedServiceForStaff(null);
  };

  const handleStaffAssignmentSaved = (staffIds) => {
    showAlert(`Staff assignments updated successfully`, "success");
    handleCloseStaffAssignment();
    // Refresh data to show updated assignment count
    fetchData();
  };

  const getCategoryColor = (category) => {
    const colors = {
      haircut: "#4A90E2",
      color: "#E94B3C",
      styling: "#F39C12",
      treatment: "#9B59B6",
      spa: "#1ABC9C",
      nails: "#E91E63",
      makeup: "#FF6B9D",
      waxing: "#8E44AD",
      other: "#95A5A6",
    };
    return colors[category] || colors.other;
  };

  if (loading) {
    return (
      <SidebarLayout>
        <div className="bookings-container services-page">
          <div className="loading-state">Loading service templates...</div>
        </div>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout>
      <div
        className="bookings-container services-page"
        style={{
          overflowX: "hidden",
          width: "100%",
          maxWidth: "100%",
          boxSizing: "border-box",
        }}
      >
        {/* Alert */}
        {alert.show && (
          <div className={`alert alert-${alert.type}`}>{alert.message}</div>
        )}

        {/* Header */}
        <div
          className="bookings-header"
          style={{
            flexDirection: isMobile ? "column" : "row",
            alignItems: isMobile ? "stretch" : "center",
            gap: isMobile ? "16px" : "0",
          }}
        >
          <div>
            <h1
              style={{
                margin: "0 0 8px 0",
                fontSize: "22px",
                fontWeight: "600",
                color: "#1a1a1a",
                fontFamily: "Inter, sans-serif",
              }}
            >
              Shop Service Templates
            </h1>
            <p
              style={{
                margin: 0,
                fontSize: "14px",
                color: "#666",
                fontFamily: "Inter, sans-serif",
              }}
            >
              Create service templates that professionals can use when setting
              up their services
            </p>
          </div>
          <div
            style={{
              display: "flex",
              gap: "12px",
              flexWrap: "wrap",
              width: isMobile ? "100%" : "auto",
            }}
          >
            {selectedServices.length > 0 && (
              <button
                onClick={handleBulkDelete}
                style={{
                  width: isMobile ? "100%" : "auto",
                  fontSize: "14px",
                  fontWeight: "600",
                  fontFamily: "Inter, sans-serif",
                  padding: "10px 20px",
                  backgroundColor: "#dc2626",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                }}
              >
                🗑️ Delete Selected ({selectedServices.length})
              </button>
            )}
            <button
              className="btn-secondary"
              onClick={() => setShowImportModal(true)}
              style={{
                width: isMobile ? "100%" : "auto",
                fontSize: "14px",
                fontWeight: "600",
                fontFamily: "Inter, sans-serif",
                padding: "10px 20px",
              }}
            >
              📥 Import CSV
            </button>
            <button
              className="btn-secondary"
              onClick={handleToggleTemplates}
              style={{
                width: isMobile ? "100%" : "auto",
                fontSize: "14px",
                fontWeight: "600",
                fontFamily: "Inter, sans-serif",
                padding: "10px 20px",
                background: showTemplates ? "#667eea" : "#fff",
                color: showTemplates ? "#fff" : "#333",
                border: showTemplates ? "none" : "1px solid #d1d5db",
              }}
            >
              📚 {showTemplates ? "Hide" : "Browse"} Platform Templates
            </button>
            <button
              className="btn btn-primary"
              onClick={handleCreate}
              style={{
                width: isMobile ? "100%" : "auto",
                fontSize: "14px",
                fontWeight: "600",
                fontFamily: "Inter, sans-serif",
                padding: "10px 20px",
              }}
            >
              + Create Custom
            </button>
          </div>
        </div>

        {/* Platform Templates Section - Collapsible */}
        {showTemplates && (
          <div
            style={{
              marginBottom: "24px",
              padding: "24px",
              background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
              borderRadius: "12px",
              border: "2px solid #667eea",
            }}
          >
            <h3
              style={{
                margin: "0 0 8px 0",
                fontSize: "18px",
                fontWeight: "600",
                color: "#001d4a",
                fontFamily: "Inter, sans-serif",
              }}
            >
              ✨ Quick Start: Add Services from Our Library
            </h3>
            <p
              style={{
                margin: "0 0 20px 0",
                fontSize: "14px",
                color: "#4b5563",
                lineHeight: "1.5",
                fontFamily: "Inter, sans-serif",
              }}
            >
              Browse our collection of common beauty & wellness services. Select
              one or more templates and add them to your business. You can
              customize pricing, duration, and descriptions later to fit your
              brand.
            </p>

            {/* Search and Filter */}
            <div
              style={{
                display: "flex",
                gap: "12px",
                marginBottom: "20px",
                flexDirection: isMobile ? "column" : "row",
              }}
            >
              <input
                type="text"
                placeholder="Search templates..."
                value={templateSearchTerm}
                onChange={(e) => setTemplateSearchTerm(e.target.value)}
                style={{
                  flex: isMobile ? "none" : 1,
                  padding: "10px 14px",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  fontSize: "14px",
                  fontFamily: "Inter, sans-serif",
                  outline: "none",
                }}
              />
              <select
                value={templateCategoryFilter}
                onChange={(e) => setTemplateCategoryFilter(e.target.value)}
                style={{
                  flex: isMobile ? "none" : "0 0 200px",
                  padding: "10px 14px",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  fontSize: "14px",
                  fontFamily: "Inter, sans-serif",
                  outline: "none",
                  background: "#fff",
                }}
              >
                <option value="all">All Categories</option>
                {templateCategoryOptions.map((cat) => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Templates Grid */}
            {loadingTemplates ? (
              <div
                style={{ textAlign: "center", padding: "40px", color: "#666" }}
              >
                Loading templates...
              </div>
            ) : templatesError ? (
              <div
                style={{
                  background: "#fff3f3",
                  border: "1px solid #c00",
                  borderRadius: "8px",
                  padding: "16px",
                  color: "#c00",
                }}
              >
                {templatesError}
              </div>
            ) : filteredPlatformTemplates.length === 0 ? (
              <div
                style={{ textAlign: "center", padding: "40px", color: "#666" }}
              >
                No templates found. Try different filters.
              </div>
            ) : (
              <>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: isMobile
                      ? "1fr"
                      : "repeat(auto-fill, minmax(280px, 1fr))",
                    gap: "16px",
                    marginBottom: "20px",
                    maxHeight: "500px",
                    overflowY: "auto",
                    padding: "4px",
                  }}
                >
                  {filteredPlatformTemplates.map((template) => {
                    const templateId = getTemplateId(template);
                    const isSelected = selectedTemplates.includes(templateId);
                    return (
                      <div
                        key={templateId}
                        onClick={() =>
                          handleToggleTemplateSelection(templateId)
                        }
                        style={{
                          padding: "16px",
                          background: isSelected ? "#f0f9ff" : "#fff",
                          border: isSelected
                            ? "2px solid #667eea"
                            : "1px solid #e5e7eb",
                          borderRadius: "10px",
                          cursor: "pointer",
                          transition: "all 0.2s",
                          position: "relative",
                        }}
                        onMouseEnter={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.borderColor = "#c7d2fe";
                            e.currentTarget.style.boxShadow =
                              "0 2px 8px rgba(102, 126, 234, 0.15)";
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.borderColor = "#e5e7eb";
                            e.currentTarget.style.boxShadow = "none";
                          }
                        }}
                      >
                        {/* Checkbox */}
                        <div
                          style={{
                            position: "absolute",
                            top: "12px",
                            right: "12px",
                            width: "24px",
                            height: "24px",
                            background: isSelected ? "#667eea" : "#fff",
                            border: isSelected ? "none" : "2px solid #d1d5db",
                            borderRadius: "6px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "14px",
                            color: "#fff",
                            fontWeight: "bold",
                          }}
                        >
                          {isSelected && "✓"}
                        </div>

                        <h4
                          style={{
                            margin: "0 0 8px 0",
                            fontSize: "16px",
                            fontWeight: "600",
                            color: "#001d4a",
                            fontFamily: "Inter, sans-serif",
                            paddingRight: "32px",
                          }}
                        >
                          {template.name}
                        </h4>
                        {template.description && (
                          <p
                            style={{
                              margin: "0 0 12px 0",
                              fontSize: "13px",
                              color: "#6b7280",
                              lineHeight: "1.4",
                              fontFamily: "Inter, sans-serif",
                            }}
                          >
                            {template.description}
                          </p>
                        )}
                        <div
                          style={{
                            display: "flex",
                            gap: "8px",
                            flexWrap: "wrap",
                            fontSize: "12px",
                          }}
                        >
                          {template.suggestedPrice && (
                            <span
                              style={{
                                padding: "4px 10px",
                                background: "#e8f5e9",
                                color: "#2e7d32",
                                borderRadius: "6px",
                                fontWeight: "600",
                                fontFamily: "Inter, sans-serif",
                              }}
                            >
                              ${template.suggestedPrice}
                            </span>
                          )}
                          {template.suggestedDuration && (
                            <span
                              style={{
                                padding: "4px 10px",
                                background: "#e3f2fd",
                                color: "#1565c0",
                                borderRadius: "6px",
                                fontFamily: "Inter, sans-serif",
                              }}
                            >
                              {template.suggestedDuration} min
                            </span>
                          )}
                          <span
                            style={{
                              padding: "4px 10px",
                              background: "#f3e5f5",
                              color: "#6a1b9a",
                              borderRadius: "6px",
                              fontFamily: "Inter, sans-serif",
                            }}
                          >
                            {toUserFriendly(template.category)}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Add Selected Button */}
                <div style={{ textAlign: "center" }}>
                  <button
                    onClick={handleBulkAddTemplates}
                    disabled={selectedTemplates.length === 0}
                    style={{
                      padding: "14px 32px",
                      background:
                        selectedTemplates.length > 0
                          ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                          : "#e5e7eb",
                      color: selectedTemplates.length > 0 ? "#fff" : "#9ca3af",
                      border: "none",
                      borderRadius: "10px",
                      fontSize: "15px",
                      fontWeight: "600",
                      cursor:
                        selectedTemplates.length > 0
                          ? "pointer"
                          : "not-allowed",
                      fontFamily: "Inter, sans-serif",
                      transition: "all 0.2s",
                      boxShadow:
                        selectedTemplates.length > 0
                          ? "0 4px 12px rgba(102, 126, 234, 0.3)"
                          : "none",
                    }}
                    onMouseEnter={(e) => {
                      if (selectedTemplates.length > 0) {
                        e.target.style.transform = "translateY(-2px)";
                        e.target.style.boxShadow =
                          "0 6px 16px rgba(102, 126, 234, 0.4)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedTemplates.length > 0) {
                        e.target.style.transform = "translateY(0)";
                        e.target.style.boxShadow =
                          "0 4px 12px rgba(102, 126, 234, 0.3)";
                      }
                    }}
                  >
                    ✓ Add Selected to My Services ({selectedTemplates.length})
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Filters */}
        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            gap: "16px",
            marginBottom: "24px",
            width: "100%",
            maxWidth: "100%",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              flex: isMobile ? "0 0 100%" : "1 1 300px",
              minWidth: 0,
              width: isMobile ? "100%" : "auto",
            }}
          >
            <input
              type="text"
              placeholder="Search templates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: "100%",
                padding: "10px 14px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
                fontFamily: "Inter, sans-serif",
                outline: "none",
                boxSizing: "border-box",
              }}
            />
          </div>
          <div
            style={{
              flex: isMobile ? "0 0 100%" : "1 1 200px",
              minWidth: 0,
              width: isMobile ? "100%" : "auto",
            }}
          >
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              style={{
                width: "100%",
                padding: "10px 14px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
                fontFamily: "Inter, sans-serif",
                outline: "none",
                backgroundColor: "white",
                boxSizing: "border-box",
              }}
            >
              <option value="all">All Categories</option>
              {serviceCategories.map((cat) => (
                <option key={cat.value} value={cat.value}>
                  {cat.label}
                </option>
              ))}
            </select>
          </div>
          <div
            style={{
              flex: isMobile ? "0 0 100%" : "1 1 150px",
              minWidth: 0,
              width: isMobile ? "100%" : "auto",
            }}
          >
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{
                width: "100%",
                padding: "10px 14px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
                fontFamily: "Inter, sans-serif",
                outline: "none",
                backgroundColor: "white",
                boxSizing: "border-box",
              }}
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>

        {/* Templates Grid */}
        {filteredTemplates.length === 0 ? (
          <div className="empty-state">
            <p>No service templates found</p>
            <p className="empty-state-subtitle">
              Create templates that professionals can use to quickly set up
              their services
            </p>
            <button className="btn btn-primary" onClick={handleCreate}>
              Create Your First Template
            </button>
          </div>
        ) : (
          <div
            style={{
              display: "grid",
              /**
               * FIVE PER ROW WAS A GUESS ABOUT THE SCREEN (reported 2026-10-23: *"for smaller device like iPad and
               * tablets the service template page … have the service templates squeezed together … Its forcing 5
               * service templates per row"*). A fixed count cannot know the width, so a 1024pt iPad landscape got
               * five ~180pt cards with the names running into each other. `auto-fill` with a minimum card width
               * lets the browser fit as many as actually fit — four on an iPad, five on a laptop, two on a phone
               * (the phone branch is kept exactly as it was, since that was already right).
               */
              gridTemplateColumns: isMobile
                ? "repeat(2, 1fr)"
                : "repeat(auto-fill, minmax(240px, 1fr))",
              gap: isMobile ? "16px" : "24px",
              width: "100%",
              maxWidth: "100%",
              overflow: "hidden",
            }}
          >
            {filteredTemplates.map((template) => (
              <div
                key={template._id}
                className={`service-card ${template.isActive === false ? "inactive" : ""}`}
                style={{
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  maxWidth: "100%",
                  boxSizing: "border-box",
                  overflow: "hidden",
                }}
              >
                {/* Selection Checkbox */}
                <div
                  style={{
                    position: "absolute",
                    top: "8px",
                    left: "8px",
                    zIndex: 10,
                    backgroundColor: "white",
                    borderRadius: "4px",
                    padding: "4px",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedServices.includes(template._id)}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleToggleServiceSelection(template._id);
                    }}
                    style={{
                      width: "18px",
                      height: "18px",
                      cursor: "pointer",
                    }}
                  />
                </div>
                {/* Category Badge */}
                <div
                  className="category-badge"
                  style={{
                    backgroundColor: getCategoryColor(template.category),
                  }}
                >
                  {serviceCategories.find((c) => c.value === template.category)
                    ?.label || "Other"}
                </div>

                {/* Service Image Thumbnail - Headshot Style */}
                {template.serviceImage && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      padding: isMobile ? "12px" : "16px 16px 0",
                      marginBottom: "12px",
                      maxWidth: "100%",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        width: isMobile ? "140px" : "180px",
                        height: isMobile ? "140px" : "180px",
                        borderRadius: "50%",
                        overflow: "hidden",
                        border: "4px solid #e5e7eb",
                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                        flexShrink: 0,
                      }}
                    >
                      <img
                        src={(() => {
                          const isAbsolute =
                            template.serviceImage.startsWith("http://") ||
                            template.serviceImage.startsWith("https://");
                          const url = isAbsolute
                            ? template.serviceImage
                            : `${API_BASE_URL}${template.serviceImage}`;
                          return url;
                        })()}
                        alt={template.name}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                        onError={(e) => {
                        }}
                      />
                    </div>
                  </div>
                )}

                <div
                  style={{
                    padding: template.serviceImage ? "0 16px" : "16px",
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <div
                    className="service-card-header"
                    style={{ marginBottom: "8px" }}
                  >
                    <h3
                      style={{
                        margin: 0,
                        fontSize: "16px",
                        fontWeight: "600",
                        color: "#1a1a1a",
                        fontFamily: "Inter, sans-serif",
                      }}
                    >
                      {template.name}
                    </h3>
                    <span
                      className={`status-badge ${template.isActive === false ? "inactive" : "active"}`}
                    >
                      {template.isActive === false ? "Inactive" : "Active"}
                    </span>
                  </div>

                  {template.description && (
                    <p
                      style={{
                        margin: "0 0 12px 0",
                        fontSize: "14px",
                        color: "#666",
                        fontFamily: "Inter, sans-serif",
                        lineHeight: "1.5",
                      }}
                    >
                      {template.description}
                    </p>
                  )}

                  <div style={{ marginTop: "auto" }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        marginBottom: "8px",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "13px",
                          fontFamily: "Inter, sans-serif",
                          color: "#666",
                        }}
                      >
                        ⏱️ Duration:
                      </span>
                      <span
                        style={{
                          fontSize: "13px",
                          fontFamily: "Inter, sans-serif",
                          fontWeight: "600",
                          color: "#333",
                        }}
                      >
                        {template.durationMinutes} min
                      </span>
                    </div>
                    {template.price ? (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          marginBottom: "8px",
                        }}
                      >
                        <span
                          style={{
                            fontSize: "13px",
                            fontFamily: "Inter, sans-serif",
                            color: "#666",
                          }}
                        >
                          💵 Price:
                        </span>
                        <span
                          style={{
                            fontSize: "14px",
                            fontFamily: "Inter, sans-serif",
                            fontWeight: "700",
                            color: "#10b981",
                          }}
                        >
                          ${parseFloat(template.price).toFixed(2)}
                        </span>
                      </div>
                    ) : (
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#999",
                          fontStyle: "italic",
                          fontFamily: "Inter, sans-serif",
                        }}
                      >
                        Professionals set their own pricing
                      </div>
                    )}
                  </div>
                </div>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                    marginTop: "12px",
                    padding: template.serviceImage ? "0 16px 16px" : "0 0 16px",
                  }}
                >
                  <button
                    onClick={() => handleEdit(template)}
                    style={{
                      width: "100%",
                      padding: "10px 16px",
                      fontSize: "13px",
                      fontWeight: "600",
                      fontFamily: "Inter, sans-serif",
                      color: "#666",
                      backgroundColor: "#f5f5f5",
                      border: "none",
                      borderRadius: "6px",
                      cursor: "pointer",
                    }}
                  >
                    Edit
                  </button>
                  {template.isActive ? (
                    <button
                      onClick={() => handleDeactivate(template._id)}
                      style={{
                        width: "100%",
                        padding: "10px 16px",
                        fontSize: "13px",
                        fontWeight: "600",
                        fontFamily: "Inter, sans-serif",
                        color: "white",
                        backgroundColor: "#ef4444",
                        border: "none",
                        borderRadius: "6px",
                        cursor: "pointer",
                      }}
                    >
                      Deactivate
                    </button>
                  ) : (
                    <button
                      onClick={() => handleReactivate(template)}
                      style={{
                        width: "100%",
                        padding: "10px 16px",
                        fontSize: "13px",
                        fontWeight: "600",
                        fontFamily: "Inter, sans-serif",
                        color: "white",
                        backgroundColor: "#10b981",
                        border: "none",
                        borderRadius: "6px",
                        cursor: "pointer",
                      }}
                    >
                      Reactivate
                    </button>
                  )}
                  <button
                    onClick={() => handleDeleteTemplate(template)}
                    style={{
                      width: "100%",
                      padding: "10px 16px",
                      fontSize: "13px",
                      fontWeight: "600",
                      fontFamily: "Inter, sans-serif",
                      color: "white",
                      backgroundColor: "#9ca3af",
                      border: "none",
                      borderRadius: "6px",
                      cursor: "pointer",
                    }}
                  >
                    🗑️ Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Delete Type Modal */}
        {showDeleteModal && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.6)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 2000,
              padding: "20px",
            }}
            onClick={() => setShowDeleteModal(false)}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                backgroundColor: "white",
                borderRadius: "12px",
                maxWidth: "500px",
                width: "100%",
                padding: "32px",
                boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.2)",
              }}
            >
              <h2
                style={{
                  margin: "0 0 16px 0",
                  fontSize: "20px",
                  fontWeight: "600",
                  color: "#1a1a1a",
                  fontFamily: "Inter, sans-serif",
                }}
              >
                Choose delete type:
              </h2>

              <div
                style={{
                  marginBottom: "24px",
                  padding: "16px",
                  backgroundColor: "#f9fafb",
                  borderRadius: "8px",
                  border: "1px solid #e5e7eb",
                }}
              >
                <p
                  style={{
                    margin: "0 0 12px 0",
                    fontSize: "14px",
                    color: "#4b5563",
                    fontFamily: "Inter, sans-serif",
                  }}
                >
                  You are about to delete {deleteModalData.services.length}{" "}
                  service(s):
                </p>
                <ul
                  style={{
                    margin: "0",
                    paddingLeft: "20px",
                    maxHeight: "120px",
                    overflowY: "auto",
                  }}
                >
                  {deleteModalData.services.map((service) => (
                    <li
                      key={service._id}
                      style={{
                        fontSize: "14px",
                        color: "#1f2937",
                        fontFamily: "Inter, sans-serif",
                        marginBottom: "4px",
                      }}
                    >
                      {service.name}
                    </li>
                  ))}
                </ul>
              </div>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                  marginBottom: "24px",
                }}
              >
                <button
                  onClick={() => {
                    setShowConfirmModal(false);
                    deleteModalData.onConfirm("SOFT");
                  }}
                  style={{
                    width: "100%",
                    padding: "16px",
                    backgroundColor: "#f59e0b",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    fontSize: "15px",
                    fontWeight: "600",
                    fontFamily: "Inter, sans-serif",
                    cursor: "pointer",
                    textAlign: "left",
                    transition: "all 0.2s",
                  }}
                  onMouseEnter={(e) =>
                    (e.target.style.backgroundColor = "#d97706")
                  }
                  onMouseLeave={(e) =>
                    (e.target.style.backgroundColor = "#f59e0b")
                  }
                >
                  <div style={{ fontSize: "16px", marginBottom: "4px" }}>
                    💤 Deactivate (SOFT)
                  </div>
                  <div style={{ fontSize: "13px", opacity: 0.9 }}>
                    Can be reactivated later
                  </div>
                </button>

                <button
                  onClick={() => {
                    setConfirmModalData({
                      message: (
                        <div>
                          <p
                            style={{
                              marginBottom: "16px",
                              fontWeight: "600",
                              color: "#dc2626",
                            }}
                          >
                            ⚠️ WARNING: PERMANENT DELETE
                          </p>
                          <p style={{ marginBottom: "12px" }}>
                            This will PERMANENTLY delete{" "}
                            {deleteModalData.services.length} service(s).
                          </p>
                          <p style={{ fontWeight: "600" }}>
                            This action CANNOT be undone!
                          </p>
                          <p style={{ marginTop: "16px" }}>
                            Are you absolutely sure?
                          </p>
                        </div>
                      ),
                      onConfirm: () => {
                        setShowConfirmModal(false);
                        deleteModalData.onConfirm("PERMANENT");
                      },
                      onCancel: () => {
                        setShowConfirmModal(false);
                      },
                    });
                    setShowConfirmModal(true);
                  }}
                  style={{
                    width: "100%",
                    padding: "16px",
                    backgroundColor: "#dc2626",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    fontSize: "15px",
                    fontWeight: "600",
                    fontFamily: "Inter, sans-serif",
                    cursor: "pointer",
                    textAlign: "left",
                    transition: "all 0.2s",
                  }}
                  onMouseEnter={(e) =>
                    (e.target.style.backgroundColor = "#b91c1c")
                  }
                  onMouseLeave={(e) =>
                    (e.target.style.backgroundColor = "#dc2626")
                  }
                >
                  <div style={{ fontSize: "16px", marginBottom: "4px" }}>
                    🗑️ Delete Forever (PERMANENT)
                  </div>
                  <div style={{ fontSize: "13px", opacity: 0.9 }}>
                    Cannot be undone
                  </div>
                </button>
              </div>

              <button
                onClick={() => setShowDeleteModal(false)}
                style={{
                  width: "100%",
                  padding: "12px",
                  backgroundColor: "#f3f4f6",
                  color: "#6b7280",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  fontFamily: "Inter, sans-serif",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Confirm Modal */}
        {showConfirmModal && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.6)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 2001,
              padding: "20px",
            }}
            onClick={() => {
              setShowConfirmModal(false);
              if (confirmModalData.onCancel) confirmModalData.onCancel();
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                backgroundColor: "white",
                borderRadius: "12px",
                maxWidth: "450px",
                width: "100%",
                padding: "32px",
                boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.2)",
              }}
            >
              <div
                style={{
                  marginBottom: "24px",
                  fontSize: "15px",
                  color: "#1f2937",
                  fontFamily: "Inter, sans-serif",
                  lineHeight: "1.6",
                }}
              >
                {confirmModalData.message}
              </div>

              <div style={{ display: "flex", gap: "12px" }}>
                <button
                  onClick={() => {
                    if (confirmModalData.onConfirm)
                      confirmModalData.onConfirm();
                  }}
                  style={{
                    flex: 1,
                    padding: "12px 24px",
                    backgroundColor: "#2563eb",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    fontSize: "14px",
                    fontWeight: "600",
                    fontFamily: "Inter, sans-serif",
                    cursor: "pointer",
                  }}
                >
                  OK
                </button>
                <button
                  onClick={() => {
                    setShowConfirmModal(false);
                    if (confirmModalData.onCancel) confirmModalData.onCancel();
                  }}
                  style={{
                    flex: 1,
                    padding: "12px 24px",
                    backgroundColor: "#f3f4f6",
                    color: "#6b7280",
                    border: "1px solid #d1d5db",
                    borderRadius: "8px",
                    fontSize: "14px",
                    fontWeight: "600",
                    fontFamily: "Inter, sans-serif",
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Create/Edit Modal */}
        {showModal && (
          <div
            className="modal-overlay"
            onClick={() => setShowModal(false)}
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              alignItems: isMobile ? "flex-start" : "center",
              justifyContent: "center",
              zIndex: 1000,
              overflowY: "auto",
              overflowX: "hidden",
              padding: isMobile ? "20px 10px" : "40px 20px",
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                backgroundColor: "white",
                borderRadius: "12px",
                width: isMobile ? "calc(100vw - 20px)" : "90%",
                maxWidth: "600px",
                maxHeight: isMobile ? "calc(100vh - 40px)" : "90vh",
                display: "flex",
                flexDirection: "column",
                boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
                margin: isMobile ? "0" : "auto",
                overflow: "hidden",
              }}
            >
              {/* Fixed Header */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: isMobile ? "16px" : "20px 24px",
                  borderBottom: "1px solid #e5e7eb",
                  flexShrink: 0,
                }}
              >
                <h2
                  style={{
                    margin: 0,
                    fontSize: isMobile ? "18px" : "20px",
                    fontWeight: "600",
                    color: "#1a1a1a",
                    fontFamily: "Inter, sans-serif",
                  }}
                >
                  {isEditing
                    ? "Edit Service Template"
                    : "Create Service Template"}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  style={{
                    background: "none",
                    border: "none",
                    fontSize: "24px",
                    color: "#6b7280",
                    cursor: "pointer",
                    padding: "0",
                    width: "32px",
                    height: "32px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "4px",
                  }}
                >
                  &times;
                </button>
              </div>

              <form
                onSubmit={handleSubmit}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  flex: 1,
                  minHeight: 0,
                }}
              >
                {/* Scrollable Body */}
                <div
                  style={{
                    padding: isMobile ? "16px" : "24px",
                    overflowY: "auto",
                    flex: 1,
                  }}
                >
                  <div style={{ marginBottom: "20px" }}>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#333",
                        fontFamily: "Inter, sans-serif",
                      }}
                    >
                      Service Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                      placeholder="e.g., Classic Haircut"
                      maxLength={100}
                      required
                      style={{
                        width: "100%",
                        padding: "10px 12px",
                        fontSize: "14px",
                        fontFamily: "Inter, sans-serif",
                        border: "1px solid #e5e7eb",
                        borderRadius: "8px",
                        outline: "none",
                        boxSizing: "border-box",
                      }}
                    />
                  </div>

                  <div style={{ marginBottom: "20px" }}>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#333",
                        fontFamily: "Inter, sans-serif",
                      }}
                    >
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          description: e.target.value,
                        })
                      }
                      placeholder="Describe the service..."
                      maxLength={500}
                      rows={3}
                      style={{
                        width: "100%",
                        padding: "10px 12px",
                        fontSize: "14px",
                        fontFamily: "Inter, sans-serif",
                        border: "1px solid #e5e7eb",
                        borderRadius: "8px",
                        outline: "none",
                        resize: "vertical",
                        boxSizing: "border-box",
                      }}
                    />
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                      gap: "16px",
                      marginBottom: "20px",
                    }}
                  >
                    {formData.pricingType !== "hourly" &&
                      formData.pricingType !== "range" && (
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "8px",
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "#333",
                          fontFamily: "Inter, sans-serif",
                        }}
                      >
                        {formData.pricingType === "per_unit"
                          ? "Minutes per unit *"
                          : "Duration (minutes) *"}
                      </label>
                      <input
                        type="number"
                        value={formData.durationMinutes}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            durationMinutes: parseInt(e.target.value) || 0,
                          })
                        }
                        onFocus={(e) => e.target.select()}
                        min="5"
                        step="5"
                        required
                        style={{
                          width: "100%",
                          padding: "10px 12px",
                          fontSize: "14px",
                          fontFamily: "Inter, sans-serif",
                          border: "1px solid #e5e7eb",
                          borderRadius: "8px",
                          outline: "none",
                          boxSizing: "border-box",
                        }}
                      />
                    </div>
                    )}

                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "8px",
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "#333",
                          fontFamily: "Inter, sans-serif",
                        }}
                      >
                        Pricing Type
                      </label>
                      <select
                        value={formData.pricingType}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            pricingType: e.target.value,
                          })
                        }
                        style={{
                          width: "100%",
                          padding: "10px 12px",
                          fontSize: "14px",
                          fontFamily: "Inter, sans-serif",
                          border: "1px solid #e5e7eb",
                          borderRadius: "8px",
                          outline: "none",
                          boxSizing: "border-box",
                          backgroundColor: "white",
                        }}
                      >
                        <option value="fixed">Fixed Price</option>
                        <option value="per_unit">Per Unit (e.g. per nail)</option>
                        <option value="hourly">Hourly (rate x hours)</option>
                        <option value="range">
                          Options (customer picks one)
                        </option>
                        {/* From / Variable hidden until the quote flow exists */}
                      </select>
                    </div>

                    {formData.pricingType !== "range" && (
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "8px",
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "#333",
                          fontFamily: "Inter, sans-serif",
                        }}
                      >
                        {formData.pricingType === "hourly"
                          ? "Hourly Rate ($)"
                          : formData.pricingType === "from"
                            ? "Starting Price ($)"
                            : formData.pricingType === "variable"
                              ? "Estimated Price ($)"
                              : formData.pricingType === "per_unit"
                                ? "Price per unit ($)"
                                : "Price ($)"}
                      </label>
                      <input
                        type="number"
                        value={
                          formData.pricingType === "hourly"
                            ? formData.hourlyRate
                            : formData.pricingType === "per_unit"
                              ? formData.pricePerUnit
                              : formData.price
                        }
                        onChange={(e) =>
                          formData.pricingType === "hourly"
                            ? setFormData({
                                ...formData,
                                hourlyRate: e.target.value,
                              })
                            : formData.pricingType === "per_unit"
                              ? setFormData({
                                  ...formData,
                                  pricePerUnit: e.target.value,
                                })
                              : setFormData({
                                  ...formData,
                                  price: e.target.value,
                                })
                        }
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                        style={{
                          width: "100%",
                          padding: "10px 12px",
                          fontSize: "14px",
                          fontFamily: "Inter, sans-serif",
                          border: "1px solid #e5e7eb",
                          borderRadius: "8px",
                          outline: "none",
                          boxSizing: "border-box",
                        }}
                      />
                      {formData.pricingType === "hourly" && (
                        <div
                          style={{
                            fontSize: "12px",
                            color: "#6b7280",
                            marginTop: "4px",
                            fontFamily: "Inter, sans-serif",
                          }}
                        >
                          The appointment length is the number of hours the
                          customer books (hours x 60 minutes).
                        </div>
                      )}
                      {formData.pricingType === "variable" && (
                        <div
                          style={{
                            fontSize: "12px",
                            color: "#6b7280",
                            marginTop: "4px",
                            fontFamily: "Inter, sans-serif",
                          }}
                        >
                          Optional estimate - actual price determined after
                          consultation
                        </div>
                      )}
                    </div>
                    )}
                  </div>

                  {/* F2: Hourly details (rate x hours) */}
                  {formData.pricingType === "hourly" && (
                    <div style={{ marginBottom: "20px" }}>
                      <div
                        style={{
                          background: "#f0f9ff",
                          border: "1px solid #bae6fd",
                          borderRadius: "8px",
                          padding: "14px",
                          display: "grid",
                          gap: "12px",
                        }}
                      >
                        <div style={{ maxWidth: "220px" }}>
                          <label
                            style={{
                              display: "block",
                              marginBottom: "6px",
                              fontSize: "13px",
                              fontWeight: "600",
                              color: "#333",
                              fontFamily: "Inter, sans-serif",
                            }}
                          >
                            Max hours per booking
                          </label>
                          <input
                            type="number"
                            value={formData.maxHoursPerBooking}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                maxHoursPerBooking: e.target.value,
                              })
                            }
                            min="1"
                            max="24"
                            style={{
                              width: "100%",
                              padding: "10px 12px",
                              fontSize: "14px",
                              fontFamily: "Inter, sans-serif",
                              border: "1px solid #e5e7eb",
                              borderRadius: "8px",
                              outline: "none",
                              boxSizing: "border-box",
                            }}
                          />
                        </div>
                        <div
                          style={{
                            fontSize: "12px",
                            color: "#0369a1",
                            fontFamily: "Inter, sans-serif",
                          }}
                        >
                          Customers pick how many hours they need. Total price =
                          hourly rate × hours, and the appointment length scales
                          the same way.
                        </div>
                      </div>
                    </div>
                  )}

                  {/* F1: Per-unit details (only for per_unit pricing) - only max
                      units; the Duration field above doubles as minutes per unit */}
                  {formData.pricingType === "per_unit" && (
                    <div style={{ marginBottom: "20px" }}>
                      <div
                        style={{
                          background: "#f0f9ff",
                          border: "1px solid #bae6fd",
                          borderRadius: "8px",
                          padding: "14px",
                          display: "grid",
                          gap: "12px",
                        }}
                      >
                        <div style={{ maxWidth: "220px" }}>
                          <label
                            style={{
                              display: "block",
                              marginBottom: "6px",
                              fontSize: "13px",
                              fontWeight: "600",
                              color: "#333",
                              fontFamily: "Inter, sans-serif",
                            }}
                          >
                            Max units per booking
                          </label>
                          <input
                            type="number"
                            value={formData.maxUnitsPerBooking}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                maxUnitsPerBooking: e.target.value,
                              })
                            }
                            min="1"
                            max="100"
                            style={{
                              width: "100%",
                              padding: "10px 12px",
                              fontSize: "14px",
                              fontFamily: "Inter, sans-serif",
                              border: "1px solid #e5e7eb",
                              borderRadius: "8px",
                              outline: "none",
                              boxSizing: "border-box",
                            }}
                          />
                        </div>
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: "12px",
                          }}
                        >
                          <div>
                            <label
                              style={{
                                display: "block",
                                marginBottom: "6px",
                                fontSize: "13px",
                                fontWeight: "600",
                                color: "#333",
                                fontFamily: "Inter, sans-serif",
                              }}
                            >
                              Unit label (optional)
                            </label>
                            <input
                              type="text"
                              value={formData.unitLabel}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  unitLabel: e.target.value,
                                })
                              }
                              placeholder="e.g. nail"
                              style={{
                                width: "100%",
                                padding: "10px 12px",
                                fontSize: "14px",
                                fontFamily: "Inter, sans-serif",
                                border: "1px solid #e5e7eb",
                                borderRadius: "8px",
                                outline: "none",
                                boxSizing: "border-box",
                              }}
                            />
                          </div>
                          <div></div>
                        </div>
                        <div
                          style={{
                            fontSize: "12px",
                            color: "#0369a1",
                            fontFamily: "Inter, sans-serif",
                          }}
                        >
                          For per-unit pricing, the "Minutes per unit" field above
                          is the time for ONE unit (e.g. 15 min per nail).
                          Customers pick a quantity (min 1, up to the max here).
                          Total time/price = quantity × per-unit value.
                        </div>
                      </div>
                    </div>
                  )}

                  {/* F2: Range options - the customer picks one, which becomes
                      the definite booking price */}
                  {formData.pricingType === "range" && (
                    <div style={{ marginBottom: "20px" }}>
                      <div
                        style={{
                          background: "#f0f9ff",
                          border: "1px solid #bae6fd",
                          borderRadius: "8px",
                          padding: "14px",
                          display: "grid",
                          gap: "12px",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "13px",
                            fontWeight: "600",
                            color: "#333",
                            fontFamily: "Inter, sans-serif",
                          }}
                        >
                          Price options
                        </div>
                        {(formData.priceTiers || []).map((tier, index) => (
                          <div
                            key={`tier-${index}`}
                            style={{
                              display: "grid",
                              gridTemplateColumns: "2fr 1fr 1fr auto",
                              gap: "8px",
                              alignItems: "center",
                            }}
                          >
                            <input
                              type="text"
                              value={tier.label}
                              onChange={(e) => {
                                const next = [...formData.priceTiers];
                                next[index] = { ...next[index], label: e.target.value };
                                setFormData({ ...formData, priceTiers: next });
                              }}
                              placeholder="Label, e.g. Mid-back"
                              style={{
                                width: "100%",
                                padding: "10px 12px",
                                fontSize: "14px",
                                fontFamily: "Inter, sans-serif",
                                border: "1px solid #e5e7eb",
                                borderRadius: "8px",
                                outline: "none",
                                boxSizing: "border-box",
                              }}
                            />
                            <input
                              type="number"
                              value={tier.price}
                              onChange={(e) => {
                                const next = [...formData.priceTiers];
                                next[index] = { ...next[index], price: e.target.value };
                                setFormData({ ...formData, priceTiers: next });
                              }}
                              placeholder="Price"
                              min="0"
                              step="0.01"
                              style={{
                                width: "100%",
                                padding: "10px 12px",
                                fontSize: "14px",
                                fontFamily: "Inter, sans-serif",
                                border: "1px solid #e5e7eb",
                                borderRadius: "8px",
                                outline: "none",
                                boxSizing: "border-box",
                              }}
                            />
                            <input
                              type="number"
                              value={tier.durationMinutes}
                              onChange={(e) => {
                                const next = [...formData.priceTiers];
                                next[index] = {
                                  ...next[index],
                                  durationMinutes: e.target.value,
                                };
                                setFormData({ ...formData, priceTiers: next });
                              }}
                              placeholder="Minutes"
                              min="1"
                              style={{
                                width: "100%",
                                padding: "10px 12px",
                                fontSize: "14px",
                                fontFamily: "Inter, sans-serif",
                                border: "1px solid #e5e7eb",
                                borderRadius: "8px",
                                outline: "none",
                                boxSizing: "border-box",
                              }}
                            />
                            <button
                              type="button"
                              onClick={() =>
                                setFormData({
                                  ...formData,
                                  priceTiers: formData.priceTiers.filter(
                                    (_, i) => i !== index,
                                  ),
                                })
                              }
                              style={{
                                padding: "10px 12px",
                                fontSize: "13px",
                                fontFamily: "Inter, sans-serif",
                                border: "1px solid #e5e7eb",
                                borderRadius: "8px",
                                background: "#fff",
                                color: "#b91c1c",
                                cursor: "pointer",
                              }}
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                        {(!formData.priceTiers ||
                          formData.priceTiers.length < 6) && (
                          <button
                            type="button"
                            onClick={() =>
                              setFormData({
                                ...formData,
                                priceTiers: [
                                  ...(formData.priceTiers || []),
                                  { label: "", price: "", durationMinutes: "" },
                                ],
                              })
                            }
                            style={{
                              justifySelf: "start",
                              padding: "10px 14px",
                              fontSize: "13px",
                              fontFamily: "Inter, sans-serif",
                              fontWeight: "600",
                              border: "1px solid #bae6fd",
                              borderRadius: "8px",
                              background: "#fff",
                              color: "#0369a1",
                              cursor: "pointer",
                            }}
                          >
                            + Add option
                          </button>
                        )}
                        <div
                          style={{
                            fontSize: "12px",
                            color: "#0369a1",
                            fontFamily: "Inter, sans-serif",
                          }}
                        >
                          Add 2-6 options with a price each. Customers choose one,
                          which becomes the booking price. Minutes per option is
                          optional - it sets the appointment length for that option
                          (otherwise the duration above is used).
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Consultation Required checkbox */}
                  {(formData.pricingType === "variable" ||
                    formData.pricingType === "from") && (
                    <div style={{ marginBottom: "20px" }}>
                      <label
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          cursor: "pointer",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={formData.requiresConsultation}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              requiresConsultation: e.target.checked,
                            })
                          }
                          style={{ width: "18px", height: "18px" }}
                        />
                        <span
                          style={{
                            fontSize: "14px",
                            fontFamily: "Inter, sans-serif",
                          }}
                        >
                          Requires consultation before booking
                        </span>
                      </label>
                    </div>
                  )}

                  <div style={{ marginBottom: "20px" }}>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#333",
                        fontFamily: "Inter, sans-serif",
                      }}
                    >
                      Category *
                    </label>
                    <select
                      value={formData.category}
                      onChange={(e) =>
                        setFormData({ ...formData, category: e.target.value })
                      }
                      required
                      style={{
                        width: "100%",
                        padding: "10px 12px",
                        fontSize: "14px",
                        fontFamily: "Inter, sans-serif",
                        border: "1px solid #e5e7eb",
                        borderRadius: "8px",
                        outline: "none",
                        backgroundColor: "white",
                        boxSizing: "border-box",
                      }}
                    >
                      {loadingCategories ? (
                        <option disabled>Loading categories...</option>
                      ) : (
                        serviceCategories.map((cat) => (
                          <option key={cat.value} value={cat.value}>
                            {cat.label}
                          </option>
                        ))
                      )}
                    </select>
                  </div>

                  {/* Applicable To (Professional Types) */}
                  {professionalTypes.length > 0 && (
                    <div style={{ marginBottom: "20px" }}>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "8px",
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "#333",
                          fontFamily: "Inter, sans-serif",
                        }}
                      >
                        Template Applies To
                      </label>
                      <p
                        style={{
                          fontSize: "12px",
                          color: "#666",
                          marginBottom: "10px",
                          fontFamily: "Inter, sans-serif",
                        }}
                      >
                        Leave empty if this template applies to all professional
                        types. Otherwise, select which types.
                      </p>
                      <div
                        style={{
                          display: "grid",
                          gap: "8px",
                          maxHeight: "200px",
                          overflowY: "auto",
                          border: "1px solid #e5e7eb",
                          borderRadius: "8px",
                          padding: "12px",
                        }}
                      >
                        {professionalTypes.map((type) => (
                          <label
                            key={type.value}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              padding: "8px",
                              border: "2px solid",
                              borderColor: formData.applicableTo.includes(
                                type.value,
                              )
                                ? "#001d4a"
                                : "#e5e7eb",
                              borderRadius: "6px",
                              cursor: "pointer",
                              background: formData.applicableTo.includes(
                                type.value,
                              )
                                ? "#e8f4f8"
                                : "white",
                              transition: "all 0.2s",
                              fontFamily: "Inter, sans-serif",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={formData.applicableTo.includes(
                                type.value,
                              )}
                              onChange={() =>
                                handleApplicableToToggle(type.value)
                              }
                              style={{ marginRight: "8px" }}
                            />
                            <span
                              style={{ fontSize: "1rem", marginRight: "6px" }}
                            >
                              {type.icon || "👤"}
                            </span>
                            <span
                              style={{ fontSize: "13px", fontWeight: "500" }}
                            >
                              {type.label}
                            </span>
                          </label>
                        ))}
                      </div>
                      <p
                        style={{
                          margin: "5px 0 0 0",
                          fontSize: "12px",
                          color: "#666",
                          fontFamily: "Inter, sans-serif",
                        }}
                      >
                        Selected {formData.applicableTo.length} type
                        {formData.applicableTo.length !== 1 ? "s" : ""}
                      </p>
                    </div>
                  )}

                  {/* Service Image Upload - COMMENTED OUT PER USER REQUEST
                  <div style={{ marginBottom: "20px" }}>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#333",
                        fontFamily: "Inter, sans-serif",
                      }}
                    >
                      Service Image
                    </label>
                    <input
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                      onChange={handleImageChange}
                      style={{
                        width: "100%",
                        padding: "10px 12px",
                        fontSize: "14px",
                        fontFamily: "Inter, sans-serif",
                        border: "1px solid #e5e7eb",
                        borderRadius: "8px",
                        outline: "none",
                        boxSizing: "border-box",
                      }}
                    />
                    {imagePreview && (
                      <div style={{ marginTop: "12px", textAlign: "center" }}>
                        <img
                          src={imagePreview}
                          alt="Preview"
                          style={{
                            maxWidth: isMobile ? "100%" : "200px",
                            maxHeight: "200px",
                            borderRadius: "8px",
                            objectFit: "cover",
                            border: "2px solid #e5e7eb",
                          }}
                        />
                      </div>
                    )}
                  </div>
                  END Service Image Upload */}

                  <div
                    style={{
                      padding: "12px 16px",
                      backgroundColor: "#e0f2fe",
                      borderLeft: "4px solid #0284c7",
                      borderRadius: "4px",
                      fontSize: "13px",
                      fontFamily: "Inter, sans-serif",
                      color: "#075985",
                    }}
                  >
                    <strong>Note:</strong> This is a template. Professionals
                    will set their own prices when they add this service to
                    their list.
                  </div>
                </div>

                {/* Fixed Footer */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: isMobile ? "column-reverse" : "row",
                    justifyContent: "flex-end",
                    gap: "12px",
                    padding: isMobile ? "16px" : "20px 24px",
                    borderTop: "1px solid #e5e7eb",
                    backgroundColor: "white",
                    flexShrink: 0,
                  }}
                >
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    style={{
                      padding: "10px 20px",
                      fontSize: "14px",
                      fontWeight: "600",
                      fontFamily: "Inter, sans-serif",
                      color: "#666",
                      backgroundColor: "#f5f5f5",
                      border: "none",
                      borderRadius: "8px",
                      cursor: "pointer",
                      width: isMobile ? "100%" : "auto",
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    style={{
                      padding: "10px 20px",
                      fontSize: "14px",
                      fontWeight: "600",
                      fontFamily: "Inter, sans-serif",
                      color: "white",
                      backgroundColor: "#7C3AED",
                      border: "none",
                      borderRadius: "8px",
                      cursor: "pointer",
                      width: isMobile ? "100%" : "auto",
                    }}
                  >
                    {isEditing ? "Update Template" : "Create Template"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Staff Assignment Modal */}
        {showStaffAssignment && selectedServiceForStaff && (
          <ServiceStaffAssignment
            serviceId={selectedServiceForStaff._id}
            serviceName={selectedServiceForStaff.name}
            businessId={businessId}
            onClose={handleCloseStaffAssignment}
            onSave={handleStaffAssignmentSaved}
            isMobile={isMobile}
          />
        )}

        {/* Service Import Modal */}
        <ServiceImportModal
          isOpen={showImportModal}
          onClose={() => setShowImportModal(false)}
          onSuccess={() => {
            fetchData();
          }}
          businessId={businessId}
        />

        {/* Quick Edit Panel for Newly Added Templates */}
        {showQuickEditPanel && newlyAddedTemplates.length > 0 && (
          <div
            style={{
              position: "fixed",
              top: 0,
              right: 0,
              width: isMobile ? "100%" : "450px",
              height: "100vh",
              background: "#fff",
              boxShadow: "-4px 0 24px rgba(0, 0, 0, 0.15)",
              zIndex: 1001,
              display: "flex",
              flexDirection: "column",
              animation: "slideIn 0.3s ease-out",
            }}
          >
            {/* Header */}
            <div
              style={{
                padding: "20px 24px",
                borderBottom: "1px solid #e5e7eb",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "#fff",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div>
                  <h3
                    style={{
                      margin: "0 0 4px 0",
                      fontSize: "18px",
                      fontWeight: "600",
                    }}
                  >
                    ✨ Services Added!
                  </h3>
                  <p style={{ margin: 0, fontSize: "13px", opacity: 0.9 }}>
                    {newlyAddedTemplates.length} service
                    {newlyAddedTemplates.length > 1 ? "s" : ""} ready to
                    customize
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowQuickEditPanel(false);
                    setNewlyAddedTemplates([]);
                  }}
                  style={{
                    background: "rgba(255, 255, 255, 0.2)",
                    border: "none",
                    color: "#fff",
                    fontSize: "24px",
                    cursor: "pointer",
                    width: "36px",
                    height: "36px",
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "all 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = "rgba(255, 255, 255, 0.3)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = "rgba(255, 255, 255, 0.2)";
                  }}
                >
                  ×
                </button>
              </div>
            </div>

            {/* Body */}
            <div
              style={{
                flex: 1,
                overflowY: "auto",
                padding: "20px 24px",
              }}
            >
              <p
                style={{
                  margin: "0 0 20px 0",
                  fontSize: "14px",
                  color: "#6b7280",
                  lineHeight: "1.5",
                }}
              >
                Your services have been created with default values. You can
                edit each one by clicking it in the table below, or close this
                panel to customize them later.
              </p>

              {/* List of added templates */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                }}
              >
                {newlyAddedTemplates.length === 0 ? (
                  <p
                    style={{
                      color: "#9ca3af",
                      textAlign: "center",
                      padding: "20px",
                    }}
                  >
                    No templates to display
                  </p>
                ) : (
                  newlyAddedTemplates
                    .filter((template) => template && template.name)
                    .map((template) => (
                      <div
                        key={template._id}
                        style={{
                          padding: "16px",
                          background: "#f9fafb",
                          border: "1px solid #e5e7eb",
                          borderRadius: "10px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "start",
                            marginBottom: "8px",
                          }}
                        >
                          <h4
                            style={{
                              margin: 0,
                              fontSize: "15px",
                              fontWeight: "600",
                              color: "#001d4a",
                            }}
                          >
                            {template.name}
                          </h4>
                          <span
                            style={{
                              fontSize: "11px",
                              padding: "4px 8px",
                              background: "#e8f5e9",
                              color: "#2e7d32",
                              borderRadius: "6px",
                              fontWeight: "600",
                            }}
                          >
                            ✓ Added
                          </span>
                        </div>
                        {template.description && (
                          <p
                            style={{
                              margin: "0 0 10px 0",
                              fontSize: "13px",
                              color: "#6b7280",
                              lineHeight: "1.4",
                            }}
                          >
                            {template.description}
                          </p>
                        )}
                        <div
                          style={{
                            display: "flex",
                            gap: "8px",
                            fontSize: "12px",
                            flexWrap: "wrap",
                          }}
                        >
                          {template.price && (
                            <span
                              style={{
                                padding: "4px 10px",
                                background: "#e8f5e9",
                                color: "#2e7d32",
                                borderRadius: "6px",
                                fontWeight: "600",
                              }}
                            >
                              ${template.price}
                            </span>
                          )}
                          {template.durationMinutes && (
                            <span
                              style={{
                                padding: "4px 10px",
                                background: "#e3f2fd",
                                color: "#1565c0",
                                borderRadius: "6px",
                              }}
                            >
                              {template.durationMinutes} min
                            </span>
                          )}
                          <span
                            style={{
                              padding: "4px 10px",
                              background: "#f3e5f5",
                              color: "#6a1b9a",
                              borderRadius: "6px",
                            }}
                          >
                            {toUserFriendly(template.category)}
                          </span>
                        </div>
                      </div>
                    ))
                )}
              </div>
            </div>

            {/* Footer */}
            <div
              style={{
                padding: "16px 24px",
                borderTop: "1px solid #e5e7eb",
                background: "#f9fafb",
              }}
            >
              <button
                onClick={() => {
                  setShowQuickEditPanel(false);
                  setNewlyAddedTemplates([]);
                }}
                style={{
                  width: "100%",
                  padding: "12px",
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  color: "#fff",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = "translateY(-1px)";
                  e.target.style.boxShadow =
                    "0 4px 12px rgba(102, 126, 234, 0.3)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = "translateY(0)";
                  e.target.style.boxShadow = "none";
                }}
              >
                Done - View in Table
              </button>
            </div>
          </div>
        )}
      </div>
    </SidebarLayout>
  );
};

export default Services;
